import React, { useEffect, useState } from "react";
import { BuilderAsset } from "../../entities/BuilderAsset";
import { SaveBuilderAssetRequest, BuilderAssetService } from "../../services/BuilderAssetService";
import { ConflictedAsset } from "./BuilderAssetConflictDialog";
import { ServerResponse } from "../../services/server/OldWebClient";
import { makeStyles, Typography, Button } from "@material-ui/core";

import classNames from "classnames";

const useStyles = makeStyles((theme) => ({
	container: {
		margin: 10,
		marginTop: 0,
	},
	selection: {
		backgroundColor: "#F4F4F4",
		margin: 7,
		marginTop: 0,
		padding: "5px 15px 5px 15px",
		borderRadius: 10,
		"&:hover": {
			backgroundColor: "#CECECE",
			cursor: "pointer",
		},
	},
	disabledSelection: {
		cursor: "not-allowed",
		"&:hover": {
			cursor: "not-allowed",
			backgroundColor: "#F4F4F4",
		},
	},
}));

interface Props {
	conflictedAsset: ConflictedAsset;
	onConflictResolved: (id: string, result: BuilderAsset | null) => void;
}

export function BuilderAssetConflictItem(props: Props) {
	const { conflictedAsset } = props;
	const classes = useStyles();
	const [disabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [orderedConflicts, setOrderedConflicts] = useState(
		[
			{ ...conflictedAsset.online, online: true },
			{ ...conflictedAsset.offline, online: false },
		]
        .sort((a, b) => b.dateModified.getTime() - a.dateModified.getTime())
	);

    useEffect(() => {
        const conflicts = [
			{ ...conflictedAsset.online, online: true },
			{ ...conflictedAsset.offline, online: false },
		]
        .sort((a, b) => b.dateModified.getTime() - a.dateModified.getTime());
        setOrderedConflicts(conflicts);
    }, [conflictedAsset])

	const onSelectOnline = (conflictedAsset: ConflictedAsset) => {
		if (disabled) {
			return;
		}
		if (conflictedAsset.online) {
			onUpdate(conflictedAsset.online);
		} else {
			clearFromOfflineStorage();
		}
	};

	const onSelectOffline = (conflictedAsset: ConflictedAsset) => {
		if (disabled) {
			return;
		}
		if (conflictedAsset.online) {
			onUpdate(conflictedAsset.offline);
		} else {
			onRestore(conflictedAsset.offline);
		}
	};

	const onUpdate = (builderAsset: BuilderAsset) => {
		const saveRequest: SaveBuilderAssetRequest = {
			...builderAsset,
		};
		onSave(saveRequest);
	};

	const onRestore = (builderAsset: BuilderAsset) => {
		// Remove id so it's treated like a create
		const { id, ...rest } = builderAsset;
		const saveRequest: SaveBuilderAssetRequest = {
			...rest,
		};
		onSave(saveRequest);
	};

	const onSave = async (saveRequest: SaveBuilderAssetRequest) => {
		setDisabled(true);
		const response = await BuilderAssetService.save(saveRequest);
		setDisabled(false);

		if (ServerResponse.isSuccess(response)) {
			props.onConflictResolved(response.data.id, response.data);
		} else if (ServerResponse.isModelValidation(response)) {
			setErrorMessage(`${response.errors[0].field} - ${response.errors[0].errors[0]}`);
		} else {
			setErrorMessage(`${response.statusCode} - ${response.message}`);
		}
	};

	const clearFromOfflineStorage = () => {
		props.onConflictResolved(conflictedAsset.offline.id, null);
	};

	return (
		<div className={classes.container}>
			{orderedConflicts.map((c) => (
				<div
					className={classNames(classes.selection, disabled && classes.disabledSelection)}
					onClick={() => (c.online ? onSelectOnline(conflictedAsset) : onSelectOffline(conflictedAsset))}
				>
					<Typography variant="h6" style={{ color: disabled ? "rgba(0, 0, 0, 0.50)" : undefined }}>
						{c.name}
					</Typography>
					<Typography style={{ color: disabled ? "rgba(0, 0, 0, 0.50)" : undefined }}>
						Last modified <u>{c.online ? "online" : "offline"}</u>: {c.dateModified.toLocaleString()}
					</Typography>
                    {
                        (c.lastModifiedOnOperatingSystem || c.lastModifiedFormFactor)
                        &&
                        <Typography style={{ color: disabled ? "rgba(0, 0, 0, 0.50)" : undefined }}>
						    Edited on a {c.lastModifiedOnOperatingSystem} {c.lastModifiedFormFactor}
					    </Typography>
                    }
                    
				</div>
			))}
			{errorMessage && (
				<div>
					<Typography variant="h6">{errorMessage}</Typography>
					<Button variant="contained" color="primary" onClick={clearFromOfflineStorage}>
						Clear From Offline Storage
					</Button>
				</div>
			)}
		</div>
	);
}